import { FieldValidator } from "final-form";

import { NumArrayFieldValue } from "fond/form/fields/NumArrayField";
import { TextFieldValue } from "fond/form/fields/TextField";
import { FieldValue } from "fond/form/fields/TextField/TextField";

/**
 * Can be used to validate multiple requirements on a single field
 */
export function compose<T>(...validators: FieldValidator<T>[]) {
  return (value: T, allValues: object): string | string[] | undefined => {
    for (let validator of validators) {
      const error = validator(value, allValues);
      if (error !== undefined) {
        return error;
      }
    }
    return undefined;
  };
}

/**
 * Required value validator
 */
export const required = (value?: unknown): string | undefined => {
  const nonEmpty = value !== null && value !== undefined;
  // if the value is a string, trim it to check for non-empty
  const nonEmptyIfString = typeof value === "string" ? value.trim() !== "" : true;

  if (nonEmpty && nonEmptyIfString) {
    return undefined;
  }
  return "This is a required field";
};

/**
 * Not zero value validator
 */
export const requireNonZero =
  (customMessage?: string) =>
  (value?: TextFieldValue): string | undefined => {
    return value === "0" || value === 0 ? (customMessage ?? "Value cannot be 0") : undefined;
  };

/**
 * Validate a field with a custom message for the invalid state
 * @param customMessage - The custom message to display when the field is required
 * @returns
 */
export const requiredWithMessage =
  (customMessage?: string) =>
  (value?: TextFieldValue): string | undefined => {
    const validationResult = required(value);
    if (validationResult) {
      return customMessage || validationResult;
    } else {
      return undefined;
    }
  };

/**
 * Required value validator for an array of values
 */
export const requireAll =
  (length: number) =>
  (value?: NumArrayFieldValue): Array<string | undefined> | undefined => {
    if (value?.length === length) {
      return undefined;
    } else {
      const errors = Array.from(Array(length));
      return errors.map((_, index) => (value?.[index] === undefined ? "This is a required field" : undefined));
    }
  };

/**
 * Required value validator for an array of values
 */
export const requireAllOrNone =
  (length: number) =>
  (value?: NumArrayFieldValue): Array<string | undefined> | undefined => {
    if (value == null || (value?.length === length && !value.some((val) => val === ""))) {
      return undefined;
    } else {
      const errors = Array.from(Array(length));
      return errors.map((err, index) => (value?.[index] === undefined || value?.[index] === "" ? "This is a required field" : undefined));
    }
  };

/**
 * URL Part Valid characters only
 */
export const alphanumeric = (value?: TextFieldValue): string | undefined =>
  value?.toString().match(/^[0-9a-zA-Z]+$/) ? undefined : "Please enter alphanumeric characters only";

/**
 * Numbers only
 */
export const numeric = (value?: TextFieldValue): string | undefined => {
  if (value == null || value === "") {
    // if the value is required it should be validated with the required validator
    return undefined;
  }
  if (!Array.isArray(value) && !Number.isNaN(Number(value?.toString()))) {
    return undefined;
  }
  return "Please enter a valid number";
};

/**
 * Mininum value
 */
export const minimum =
  (min: number) =>
  (value?: TextFieldValue): string | undefined => {
    if (value == null) return undefined;

    if (Number(value) < min) {
      return `Value must be ${min} or higher`;
    }

    return undefined;
  };

/**
 * Maximum value
 */
export const maximum =
  (max: number) =>
  (value?: TextFieldValue): string | undefined => {
    if (value == null) return undefined;

    if (Number(value) > max) {
      return `Value must be ${max} or lower`;
    }

    return undefined;
  };

/**
 * Mininum length of a string value
 */
export const minChars =
  (min: number) =>
  (value?: TextFieldValue): string | undefined => {
    if (value == null) return undefined;

    if (value.toString().length < min) {
      return `A minimum length of ${min} characters is required`;
    }

    return undefined;
  };

/**
 * Value must be not equal to another value
 */
export const notEqual =
  (current: number | string, error?: string) =>
  (value?: TextFieldValue): string | undefined => {
    if (current === value) {
      return error ?? `The value must be equal to ${current}`;
    }

    return undefined;
  };

/**
 * Value must be equal to another value
 */
export const equal =
  (current: number | string | null, error?: string) =>
  (value?: TextFieldValue): string | undefined => {
    if (current !== value) {
      return error ?? `The value must be equal to ${current}`;
    }

    return undefined;
  };

/**
 * Checks that an email address is valid.
 */
export const validEmailFormat = (value?: TextFieldValue): string | undefined => {
  if (value == null) return undefined;

  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return re.test(value.toString()) ? undefined : "Please enter a valid email address";
};

/**
 * Validate the protocol specified for a URL
 * @param allowedProtocols - The list of allowed protocols
 * @default allowedProtocols - ["http:", "https:"]
 * @example
 *  validateAllowedProtocols(["http:", "https:"])("http://example.com") // undefined
 * @returns
 */
export const validateAllowedProtocols =
  (allowedProtocols: string[] = ["http:", "https:"]): FieldValidator<FieldValue> =>
  (value?: TextFieldValue): string | undefined => {
    if (value == null) return undefined;

    try {
      const url = new URL(value.toString());
      if (!allowedProtocols.includes(url.protocol)) {
        return `Please enter a URL with one of the following protocols: ${allowedProtocols.join(", ")}`;
      }
    } catch {
      // URL is invalid so this validator can not be applied.
      return undefined;
    }
    return undefined;
  };

/**
 * Validate that a URL is valid by parsing it through the URL constructor.
 */
export const validUrlFormat = (value?: TextFieldValue): string | undefined => {
  if (value == null) return undefined;
  try {
    const _ = new URL(value.toString());
  } catch {
    return "Please enter a valid URL";
  }
  return undefined;
};

/**
 * Validate that a value contains syntactically valid JSON by parsing it.
 */
export const validJSON = (value?: TextFieldValue): string | undefined => {
  if (value == null) return undefined;
  try {
    JSON.parse(value.toString());
  } catch (error) {
    return "Please enter valid JSON";
  }
  return undefined;
};
